import { useEffect, useMemo, useState, memo, useRef } from "react";
import { BrowserRouter as Router, useNavigate, useLocation, Navigate } from "react-router-dom";
import { ChakraProvider, extendTheme, Box, Button, Code, Flex, Heading, Text } from "@chakra-ui/react";
import { Auth, Hub } from "aws-amplify";
import toast from "react-hot-toast";
import { Toaster } from "react-hot-toast";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import { ThemeProvider } from "styled-components";
import AppRoutes from "./AppRoutes";
import { GlobalStyles } from "./Assets/Styles/GlobalStyles";
import ErrorBoundary from "./Reusable/ErrorBoundary/ErrorBoundary";
import { vars } from "./Theme.css";
import AuthRoutes from "./AuthRoutes";
import { CookiesProvider } from "react-cookie";
import AppAccessForm from "./Components/AppAccessForm";
import { useDispatch, useSelector } from "react-redux";
import useSignout from "./hooks/useSignout";
import { customConsole } from "./utils/helper";
import { api, useLazyGetProfileQuery } from "../redux/reducers/apiSlice";
import { CookieConsent } from './Components/CookieConsent';
import FullPageNexusLogoAnimation from "./Reusable/FullPageNexusLogoAnimation/FullPageNexusLogoAnimation";
import {
  forwardClickThroughGhostLayer,
  hasOrphanedChakraModalLayers,
  isChakraModalBlockingElement,
  purgeOrphanedChakraModalLayers,
  startChakraModalGhostLayerGuard,
} from "./utils/chakraModalCleanup";
import { configureAmplifyPool } from "./utils/amplifyPools";
import { clearToken } from "../redux/reducers/authSlice";

// Move AppContent outside App to prevent it from being recreated on every render
// This is CRITICAL - if AppContent is defined inside App, it gets a new function reference
// on every render, causing AppRoutes and TalkJSSession to remount
const AppContent = memo(({ token, isCheckingAuth, isAuthenticated, profileData, profileDataError, profileFetchTimeout }: { 
  token: string | null;
  isCheckingAuth: boolean; 
  isAuthenticated: boolean;
  profileData?: { id?: string; isMeasureAdmin?: boolean } | null;
  profileDataError: any; 
  profileFetchTimeout: boolean;
}) => {
  const location = useLocation();
  
  const authRoutes = ["/login", "/measure/login", "/measure/forgot-password", "/measure/reset-password", "/signup", "/confirm-signup", "/forgot-password", "/reset-password"];
  const isOnAuthRoute = authRoutes.includes(location.pathname);
  
  // Show loader while checking auth
  if (isCheckingAuth) {
    return <FullPageNexusLogoAnimation />;
  }
  
  // Profile fetch failed: stay on auth screens (do not mount AppRoutes — it would sign the user out)
  if (token && (profileDataError || profileFetchTimeout)) {
    return <AuthRoutes />;
  }
  
  const isMeasureOperator = !!profileData?.isMeasureAdmin;
  const isMeasureAppPath = location.pathname.startsWith("/measure");

  if (isAuthenticated && isMeasureOperator && !isMeasureAppPath) {
    return <Navigate to="/measure/internal" replace />;
  }

  const measureAuthPaths = [
    "/measure/login",
    "/measure/forgot-password",
    "/measure/reset-password",
  ];
  if (
    isAuthenticated &&
    !isMeasureOperator &&
    isMeasureAppPath &&
    !measureAuthPaths.includes(location.pathname)
  ) {
    return <Navigate to="/" replace />;
  }

  // CRITICAL: If authenticated but on auth route, redirect immediately using Navigate component
  // This is synchronous and prevents AppRoutes from rendering on invalid route (404 blink)
  if (isAuthenticated && isOnAuthRoute) {
    if (location.pathname === "/measure/login") {
      return (
        <Navigate
          to={profileData?.isMeasureAdmin ? "/measure/internal" : "/"}
          replace
        />
      );
    }
    return <Navigate to="/" replace />;
  }
  
  if (isAuthenticated) {
    return (
      <>
        <AppRoutes />
        <CookieConsent />
      </>
    );
  }
  
  return <AuthRoutes />;
});

function getMissingCognitoEnvKeys() {
  const required = ["VITE_COGNITO_USER_POOL_ID", "VITE_COGNITO_USER_POOL_WEB_ID"] as const;
  return required.filter((k) => {
    // Vite exposes env on import.meta.env
    // @ts-ignore
    const val = import.meta.env?.[k];
    return !val || String(val).trim() === "" || String(val).toLowerCase() === "undefined";
  });
}

function MissingCognitoConfigScreen({ missingKeys }: { missingKeys: string[] }) {
  return (
    <Flex minH="100vh" align="center" justify="center" px={6} bg="white">
      <Box maxW="720px" w="full" borderWidth="1px" borderRadius="lg" p={6}>
        <Heading size="md" mb={3}>
          Local environment config missing
        </Heading>
        <Text mb={4}>
          App is crashing because AWS Cognito (Amplify) env variables are not set. Please add the following keys to{" "}
          <Code>.env.local</Code> in the project root and restart <Code>npm run dev</Code>.
        </Text>

        <Box mb={4}>
          <Text fontWeight="bold" mb={2}>
            Missing keys
          </Text>
          <Box as="ul" pl={5}>
            {missingKeys.map((k) => (
              <Box as="li" key={k}>
                <Code>{k}</Code>
              </Box>
            ))}
          </Box>
        </Box>

        <Text fontWeight="bold" mb={2}>
          Example
        </Text>
        <Box
          as="pre"
          p={3}
          borderRadius="md"
          bg="gray.50"
          borderWidth="1px"
          overflowX="auto"
          fontSize="sm"
          mb={4}
        >{`VITE_COGNITO_USER_POOL_ID=us-east-1_XXXXXXXXX
VITE_COGNITO_USER_POOL_WEB_ID=xxxxxxxxxxxxxxxxxxxxxxxxxx`}</Box>

        <Button onClick={() => window.location.reload()} colorScheme="orange">
          Reload after setting env
        </Button>
      </Box>
    </Flex>
  );
}

function App() {
  const missingCognitoKeys = useMemo(() => getMissingCognitoEnvKeys(), []);
  const dispatch = useDispatch();

  const token = useSelector((state: { auth: { token: string; } }) => state.auth.token)
  const { signOut } = useSignout();
  const [getProfile, { data: profileData, isLoading: profileDataIsLoading, error: profileDataError, isFetching: profileDataIsFetching }] = useLazyGetProfileQuery();
  const [profileFetchTimeout, setProfileFetchTimeout] = useState(false);
  const profileFailureHandledRef = useRef(false);
  const isAuthenticated = !!token && profileData?.id;
  
  const profileFetchInFlight = profileDataIsLoading || profileDataIsFetching;
  const profileFetchSettled = !profileFetchInFlight;
  const isCheckingAuth =
    !!token &&
    !isAuthenticated &&
    !profileFetchTimeout &&
    (profileFetchInFlight || (!profileData && !profileDataError));

  const chakraTheme = extendTheme({
    fonts: { ...vars.fonts },
    colors: {
      primary: {
        50: "#f9c7b7",
        100: "#f7b19a",
        200: "#f59c7e",
        300: "#f38662",
        400: "#f07045",
        500: vars.color.primary,
        600: "#e64712",
        700: "#ca3e10",
        800: "#ad350e",
        900: "#912d0c",
      },
    },
    sizes: { ...vars.sizes },
    breakpoints: { ...vars.breakpoints },
    components: {
      Modal: {
        defaultProps: {
          motionPreset: "none",
        },
      },
    },
    styles: {
      global: {
        a: {
          color: vars.color.primary,
        },
      },
    },
  });

  useEffect(() => {
    if (!token) {
      setProfileFetchTimeout(false);
      profileFailureHandledRef.current = false;
      return;
    }

    setProfileFetchTimeout(false);
    profileFailureHandledRef.current = false;
    dispatch(api.util.resetApiState());

    const timeoutId = setTimeout(() => {
      console.error('Profile fetch timeout - backend may be slow or unreachable');
      setProfileFetchTimeout(true);
    }, 30000);

    getProfile().finally(() => {
      clearTimeout(timeoutId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  
  // Log errors for debugging
  useEffect(() => {
    if(profileDataError) {
      console.error('Profile fetch error:', profileDataError);
    }
  }, [profileDataError])

  // After Cognito sign-in, load profile once. On failure, stop the loading loop and sign out.
  useEffect(() => {
    if (!token || !profileDataError || profileFailureHandledRef.current) {
      return;
    }

    profileFailureHandledRef.current = true;
    const isMeasurePath = window.location.pathname.startsWith("/measure");

    console.warn("Profile fetch failed or timed out while token exists.");
    toast.error(
      isMeasurePath
        ? "Signed in with Cognito, but the API could not verify your operator session. This is usually an API Gateway or CORS configuration issue on dev — contact your team or try again after the backend is updated."
        : "Could not load your profile. Please sign in again.",
      { duration: 8000 }
    );

    dispatch(clearToken());
    dispatch(api.util.resetApiState());

    (async () => {
      try {
        configureAmplifyPool(isMeasurePath ? "measureOperator" : "member");
        await Auth.signOut();
      } catch {
        // ignore — token already cleared
      }
    })();
  }, [token, profileDataError, dispatch]);

  const handleUserEvent = async (data) => {
    customConsole("log","Hub Data", data);
    customConsole("log","⚠️ Hub Event:", data?.payload.event);

    if(data?.payload){
      switch (data?.payload.event) {
        case "signIn":
          customConsole("log","⚠️ User signed in");
          break;
        case "signOut":
          customConsole("log","⚠️ Hub Event Signin: User signed out");
          break;
        case "tokenRefresh":
          customConsole("log","⚠️ Hub Event Token refresh: Token was successfully refreshed");
          break;
        case "tokenRefresh_failure":
          customConsole("log","⚠️ Hub Event Session expired or refresh token failed");
          signOut();
          break;
        default:
          break;
      }
    }
  
  };

  useEffect(() => {
    Hub.listen("auth", handleUserEvent);
  }, []);

  useEffect(() => startChakraModalGhostLayerGuard(), []);

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (hasOrphanedChakraModalLayers()) {
        purgeOrphanedChakraModalLayers({ force: true });
      }

      const target = e.target as HTMLElement | null;
      const top = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      const topIsSidebarBackdrop =
        top?.getAttribute?.("aria-label") === "Close navigation menu";
      const isGhostModalLayer =
        isChakraModalBlockingElement(target) || isChakraModalBlockingElement(top);
      if (!isGhostModalLayer && !topIsSidebarBackdrop) return;

      purgeOrphanedChakraModalLayers({ force: true });
      forwardClickThroughGhostLayer(e.clientX, e.clientY);
      e.preventDefault();
      e.stopImmediatePropagation();
    };

    document.addEventListener("mousedown", onMouseDown, true);
    return () => document.removeEventListener("mousedown", onMouseDown, true);
  }, []);


  if (
    window.localStorage.getItem("app_access") !== "true" &&
    // @ts-ignore
    import.meta.env.MODE === "staging"
  ) {
    return <AppAccessForm />;
  }

  if (missingCognitoKeys.length > 0) {
    return <MissingCognitoConfigScreen missingKeys={missingCognitoKeys} />;
  }

  return (
    <ChakraProvider theme={chakraTheme}>
      <CookiesProvider>
        {/* @ts-ignore */}
        <ThemeProvider theme={vars}>
          {/* @ts-ignore */}
          <GlobalStyles />
          <Router>
            <ErrorBoundary>
              <AppContent 
                token={token}
                isCheckingAuth={isCheckingAuth}
                isAuthenticated={isAuthenticated}
                profileData={profileData}
                profileDataError={profileDataError}
                profileFetchTimeout={profileFetchTimeout}
              />
            </ErrorBoundary>
          </Router>
        </ThemeProvider>
        <Toaster />
      </CookiesProvider>
    </ChakraProvider>
  );
}

export default App;